import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Trash2, Plus, Save, Eye, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WHERE_OPTIONS = [
  { id: 'event', label: 'What someone has done (or not done)' },
  { id: 'property', label: 'Properties about someone' },
  { id: 'list', label: 'If someone is in or not in a list' },
];

const TIME_MODES = [
  { id: 'over_all_time', label: 'over all time', needsValue: false },
  { id: 'within_last', label: 'within last', needsValue: true, unit: true },
  { id: 'not_within_last', label: 'not within last', needsValue: true, unit: true },
  { id: 'between', label: 'between', needsValue: true, needsValue2: true },
  { id: 'before', label: 'before', needsValue: true },
  { id: 'after', label: 'after', needsValue: true },
];

const AGGREGATOR_OPS = ['>=', '<=', '==', '!=', '>', '<'];

const emptyRule = () => ({ operator: 'AND', conditions: [] });

const newLeaf = (type) => {
  if (type === 'event') return { type: 'event', event: '', aggregator: 'count', aggregator_op: '>=', aggregator_value: 1, time_window: { mode: 'over_all_time' } };
  if (type === 'property') return { type: 'property', property: '', operator: 'equals', value: '' };
  if (type === 'list') return { type: 'list', list_id: '', is_member: true };
  return { type };
};

const SegmentBuilderPage = () => {
  const { segmentId } = useParams();
  const navigate = useNavigate();
  const isNew = !segmentId || segmentId === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rule, setRule] = useState(emptyRule());
  const [metadata, setMetadata] = useState(null);
  const [staticLists, setStaticLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, listsRes] = await Promise.all([
        axios.get(`${API}/segments/metadata`, { headers }),
        axios.get(`${API}/segments/static-lists/all`, { headers }),
      ]);
      setMetadata(metaRes.data);
      setStaticLists(listsRes.data.lists || []);
      if (!isNew) {
        const r = await axios.get(`${API}/segments/${segmentId}`, { headers });
        setName(r.data.name || '');
        setDescription(r.data.description || '');
        setRule(r.data.rule || emptyRule());
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load builder metadata');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentId, isNew]);

  useEffect(() => { load(); }, [load]);

  const addCondition = (type) => {
    setRule({ ...rule, conditions: [...(rule.conditions || []), newLeaf(type)] });
  };

  const addGroup = () => {
    setRule({ ...rule, conditions: [...(rule.conditions || []), { type: 'group', group_operator: 'AND', conditions: [newLeaf('property')] }] });
  };

  const updateConditionAt = (index, patch) => {
    const next = [...rule.conditions];
    next[index] = { ...next[index], ...patch };
    setRule({ ...rule, conditions: next });
  };

  const removeConditionAt = (index) => {
    const next = rule.conditions.filter((_, i) => i !== index);
    setRule({ ...rule, conditions: next });
  };

  const updateNestedAt = (parentIdx, childIdx, patch) => {
    const next = [...rule.conditions];
    const parent = { ...next[parentIdx] };
    const children = [...(parent.conditions || [])];
    children[childIdx] = { ...children[childIdx], ...patch };
    parent.conditions = children;
    next[parentIdx] = parent;
    setRule({ ...rule, conditions: next });
  };

  const removeNestedAt = (parentIdx, childIdx) => {
    const next = [...rule.conditions];
    const parent = { ...next[parentIdx] };
    parent.conditions = (parent.conditions || []).filter((_, i) => i !== childIdx);
    next[parentIdx] = parent;
    setRule({ ...rule, conditions: next });
  };

  const addNestedCondition = (parentIdx, type) => {
    const next = [...rule.conditions];
    const parent = { ...next[parentIdx] };
    parent.conditions = [...(parent.conditions || []), newLeaf(type)];
    next[parentIdx] = parent;
    setRule({ ...rule, conditions: next });
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/segments/preview`, { rule }, { headers });
      setPreview(r.data);
    } catch (e) {
      toast.error('Preview failed');
    } finally { setPreviewing(false); }
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Segment name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await axios.post(`${API}/segments`, { name, description, rule }, { headers });
        toast.success('Segment created');
        navigate(`/customers/segments/builder/${r.data.segment.id}`, { replace: true });
      } else {
        await axios.put(`${API}/segments/${segmentId}`, { name, description, rule }, { headers });
        toast.success('Saved');
      }
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading || !metadata) {
    return <div className="p-8 text-gray-400">Loading builder…</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="segment-builder-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers/segments')} data-testid="back-to-segments">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-white">{isNew ? 'New Segment' : 'Edit Segment'}</h1>
            <p className="text-xs text-gray-500">Build a live audience from events, properties and lists.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-1" /> {previewing ? 'Counting…' : 'Preview'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90" data-testid="save-segment-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Segment'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <Label className="text-white">Segment Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-wt-bg mt-1" placeholder="Enter segment name" data-testid="segment-name-input" />
            <div className="mt-3">
              <Label className="text-white">Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-wt-bg mt-1" />
            </div>
          </div>

          <div className="p-4 bg-wt-surface border border-wt-border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">Segment Criteria</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Customer must match {rule.operator === 'AND' ? <span className="text-purple-300">ALL</span> : <span className="text-purple-300">ANY</span>} conditions
                </div>
              </div>
              <Select value={rule.operator} onValueChange={(v) => setRule({ ...rule, operator: v })}>
                <SelectTrigger className="w-28 bg-wt-bg" data-testid="root-operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 space-y-3">
              {(rule.conditions || []).length === 0 && (
                <div className="text-center text-sm text-gray-500 py-6 border border-dashed border-wt-border rounded-lg">
                  Add your first condition below. Click "Add Filter Rule" to start.
                </div>
              )}
              {(rule.conditions || []).map((cond, idx) => (
                <ConditionRow
                  key={idx}
                  cond={cond}
                  metadata={metadata}
                  staticLists={staticLists}
                  onUpdate={(patch) => updateConditionAt(idx, patch)}
                  onRemove={() => removeConditionAt(idx)}
                  onNestedUpdate={(ci, p) => updateNestedAt(idx, ci, p)}
                  onNestedRemove={(ci) => removeNestedAt(idx, ci)}
                  onNestedAdd={(t) => addNestedCondition(idx, t)}
                />
              ))}
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => addCondition('event')} data-testid="add-event-btn">
                <Plus className="w-3 h-3 mr-1" /> Event Filter
              </Button>
              <Button size="sm" variant="outline" onClick={() => addCondition('property')} data-testid="add-property-btn">
                <Plus className="w-3 h-3 mr-1" /> Property Filter
              </Button>
              <Button size="sm" variant="outline" onClick={() => addCondition('list')} data-testid="add-list-btn">
                <Plus className="w-3 h-3 mr-1" /> List Membership
              </Button>
              <Button size="sm" variant="outline" onClick={addGroup} data-testid="add-group-btn">
                <Layers className="w-3 h-3 mr-1" /> Add Group (nested AND/OR)
              </Button>
            </div>
          </div>
        </div>

        {/* Preview panel */}
        <div className="lg:col-span-1">
          <div className="p-4 bg-wt-surface border border-wt-border rounded-lg sticky top-4">
            <div className="text-sm font-semibold text-white">Preview</div>
            <div className="text-xs text-gray-500 mt-1">Click "Preview" to see how many customers match.</div>
            <div className="mt-4 text-3xl font-bold text-purple-300 text-center py-6" data-testid="preview-count">
              {preview ? (preview.count ?? 0).toLocaleString() : '—'}
            </div>
            <div className="text-center text-xs text-gray-500 -mt-3">matching customers</div>
            {preview?.sample?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Sample</div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {preview.sample.map(s => (
                    <div key={s.id} className="text-xs p-2 bg-wt-bg rounded border border-wt-border">
                      <div className="text-white truncate">{[s.first_name, s.last_name].filter(Boolean).join(' ') || s.email || s.phone || s.id}</div>
                      <div className="text-gray-500 truncate">{s.email || s.phone}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Condition row component ============

const ConditionRow = ({ cond, metadata, staticLists, onUpdate, onRemove, onNestedUpdate, onNestedRemove, onNestedAdd }) => {
  if (cond.type === 'group') {
    return (
      <div className="p-3 bg-wt-paper border border-purple-500/30 rounded-lg" data-testid="group-condition">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-purple-300 border-purple-500/50">Group</Badge>
            <Select value={cond.group_operator || 'AND'} onValueChange={(v) => onUpdate({ group_operator: v })}>
              <SelectTrigger className="w-24 h-8 bg-wt-bg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">AND</SelectItem>
                <SelectItem value="OR">OR</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-gray-500">match inside this group</span>
          </div>
          <Button size="sm" variant="ghost" onClick={onRemove}><Trash2 className="w-4 h-4 text-red-400" /></Button>
        </div>
        <div className="space-y-2 pl-2 border-l-2 border-purple-500/30">
          {(cond.conditions || []).map((child, ci) => (
            <LeafRow
              key={ci}
              cond={child}
              metadata={metadata}
              staticLists={staticLists}
              onUpdate={(p) => onNestedUpdate(ci, p)}
              onRemove={() => onNestedRemove(ci)}
            />
          ))}
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="ghost" onClick={() => onNestedAdd('event')}><Plus className="w-3 h-3 mr-1" /> Event</Button>
            <Button size="sm" variant="ghost" onClick={() => onNestedAdd('property')}><Plus className="w-3 h-3 mr-1" /> Property</Button>
            <Button size="sm" variant="ghost" onClick={() => onNestedAdd('list')}><Plus className="w-3 h-3 mr-1" /> List</Button>
          </div>
        </div>
      </div>
    );
  }
  return <LeafRow cond={cond} metadata={metadata} staticLists={staticLists} onUpdate={onUpdate} onRemove={onRemove} />;
};

const LeafRow = ({ cond, metadata, staticLists, onUpdate, onRemove }) => {
  const tw = cond.time_window || { mode: 'over_all_time' };
  const selectedProp = (metadata.properties || []).find(p => p.id === cond.property);
  const opsForProp = selectedProp ? (metadata.property_operators[selectedProp.type] || []) : [];

  return (
    <div className="p-3 bg-wt-paper border border-wt-border rounded-lg" data-testid={`leaf-${cond.type}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-gray-500 pt-2 min-w-[50px]">WHERE</div>
        <Select value={cond.type} onValueChange={(v) => onUpdate(newLeaf(v))}>
          <SelectTrigger className="bg-wt-bg flex-1 min-w-[260px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WHERE_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onRemove}><Trash2 className="w-4 h-4 text-red-400" /></Button>
      </div>

      {/* EVENT */}
      {cond.type === 'event' && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 min-w-[50px]">Has</span>
            <Select value={cond.aggregator || 'count'} onValueChange={(v) => onUpdate({ aggregator: v })}>
              <SelectTrigger className="w-32 bg-wt-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="count">done (count)</SelectItem>
                <SelectItem value="sum">done (sum)</SelectItem>
                <SelectItem value="not_done">NOT done</SelectItem>
              </SelectContent>
            </Select>
            <Select value={cond.event || ''} onValueChange={(v) => onUpdate({ event: v })}>
              <SelectTrigger className="bg-wt-bg flex-1 min-w-[200px]"><SelectValue placeholder="Choose event…" /></SelectTrigger>
              <SelectContent>
                {Object.entries((metadata.events || []).reduce((acc, e) => { (acc[e.group] ||= []).push(e); return acc; }, {})).map(([group, evts]) => (
                  <React.Fragment key={group}>
                    <div className="px-2 py-1 text-xs text-gray-500 uppercase">{group}</div>
                    {evts.map(e => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            {cond.aggregator !== 'not_done' && (
              <>
                <Select value={cond.aggregator_op || '>='} onValueChange={(v) => onUpdate({ aggregator_op: v })}>
                  <SelectTrigger className="w-20 bg-wt-bg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGGREGATOR_OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={cond.aggregator_value ?? 1}
                  onChange={(e) => onUpdate({ aggregator_value: Number(e.target.value) })}
                  className="w-24 bg-wt-bg"
                  min="0"
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 min-w-[50px]">When</span>
            <Select value={tw.mode} onValueChange={(v) => onUpdate({ time_window: { ...tw, mode: v } })}>
              <SelectTrigger className="w-44 bg-wt-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIME_MODES.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {TIME_MODES.find(m => m.id === tw.mode)?.needsValue && (
              <>
                {['before', 'after', 'between'].includes(tw.mode) ? (
                  <DateInput
                    value={tw.value || ''}
                    onChange={(e) => onUpdate({ time_window: { ...tw, value: e.target.value } })}
                    className="w-40 bg-wt-bg"
                  />
                ) : (
                  <Input
                    type="number"
                    value={tw.value || ''}
                    onChange={(e) => onUpdate({ time_window: { ...tw, value: e.target.value } })}
                    className="w-40 bg-wt-bg"
                    placeholder={tw.mode === 'within_last' ? '30' : ''}
                  />
                )}
                {TIME_MODES.find(m => m.id === tw.mode)?.unit && (
                  <Select value={tw.unit || 'days'} onValueChange={(v) => onUpdate({ time_window: { ...tw, unit: v } })}>
                    <SelectTrigger className="w-24 bg-wt-bg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">hours</SelectItem>
                      <SelectItem value="days">days</SelectItem>
                      <SelectItem value="months">months</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {TIME_MODES.find(m => m.id === tw.mode)?.needsValue2 && (
                  <>
                    <span className="text-xs text-gray-500">and</span>
                    <DateInput
                      value={tw.value2 || ''}
                      onChange={(e) => onUpdate({ time_window: { ...tw, value2: e.target.value } })}
                      className="w-40 bg-wt-bg"
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* PROPERTY */}
      {cond.type === 'property' && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Select value={cond.property || ''} onValueChange={(v) => onUpdate({ property: v, operator: 'equals', value: '' })}>
            <SelectTrigger className="bg-wt-bg flex-1 min-w-[200px]"><SelectValue placeholder="Choose property…" /></SelectTrigger>
            <SelectContent>
              {(metadata.properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cond.operator || 'equals'} onValueChange={(v) => onUpdate({ operator: v })}>
            <SelectTrigger className="w-36 bg-wt-bg"><SelectValue /></SelectTrigger>
            <SelectContent>
              {opsForProp.map(op => <SelectItem key={op} value={op}>{op.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedProp?.type === 'bool' ? (
            <Select value={String(cond.value ?? true)} onValueChange={(v) => onUpdate({ value: v === 'true' })}>
              <SelectTrigger className="w-24 bg-wt-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          ) : cond.operator !== 'exists' && cond.operator !== 'not_exists' && (
            selectedProp?.type === 'date' ? (
              <DateInput
                value={cond.value ?? ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                className="flex-1 min-w-[150px] bg-wt-bg"
              />
            ) : (
              <Input
                value={cond.value ?? ''}
                onChange={(e) => onUpdate({ value: selectedProp?.type === 'number' ? Number(e.target.value) : e.target.value })}
                type={selectedProp?.type === 'number' ? 'number' : 'text'}
                className="flex-1 min-w-[150px] bg-wt-bg"
                placeholder="Value"
              />
            )
          )}
          {cond.operator === 'between' && (
            <>
              <span className="text-xs text-gray-500">and</span>
              {selectedProp?.type === 'number' ? (
                <Input
                  value={cond.value2 ?? ''}
                  onChange={(e) => onUpdate({ value2: Number(e.target.value) })}
                  type="number"
                  className="w-40 bg-wt-bg"
                />
              ) : (
                <DateInput
                  value={cond.value2 ?? ''}
                  onChange={(e) => onUpdate({ value2: e.target.value })}
                  className="w-40 bg-wt-bg"
                />
              )}
            </>
          )}
        </div>
      )}

      {/* LIST */}
      {cond.type === 'list' && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Select value={String(cond.is_member ?? true)} onValueChange={(v) => onUpdate({ is_member: v === 'true' })}>
            <SelectTrigger className="w-28 bg-wt-bg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Is in</SelectItem>
              <SelectItem value="false">Is NOT in</SelectItem>
            </SelectContent>
          </Select>
          <Select value={cond.list_id || ''} onValueChange={(v) => onUpdate({ list_id: v })}>
            <SelectTrigger className="bg-wt-bg flex-1 min-w-[200px]"><SelectValue placeholder="Choose a list…" /></SelectTrigger>
            <SelectContent>
              {staticLists.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No static lists yet. Create one under the Static Lists tab.</div>
              ) : staticLists.map(l => <SelectItem key={l.id} value={l.id}>{l.name} · {l.member_count || 0}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default SegmentBuilderPage;
